import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Calculator, FileText, Users, Megaphone, Plus, Trash2, Copy, Send,
  Loader2, ExternalLink, ArrowRight, DollarSign, AlertTriangle,
  PenSquare, CheckCircle2, XCircle, Eye, Mail, RefreshCw,
} from "lucide-react";
import { currency } from "@/lib/constants";

const TABS = [
  { key: "mao", label: "MAO Calculator", icon: Calculator },
  { key: "contract", label: "Assignment Contract", icon: FileText },
  { key: "buyers", label: "Cash Buyers", icon: Users },
  { key: "deals", label: "Deal Marketplace", icon: Megaphone },
  { key: "signatures", label: "Signed Docs", icon: PenSquare },
];

export default function WholesalePage() {
  const [tab, setTab] = useState("mao");

  return (
    <div className="space-y-6" data-testid="wholesale-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Wholesaler Suite</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Contract to Close.</h1>
        <p className="text-[13px] text-[#b8ac96] mt-2 max-w-2xl">
          Everything you need to lock a distressed deal, assign it to a cash buyer, and get paid — all inside The Close Out.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#33291f]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`wholesale-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wider border-b-2 ${
              tab === t.key
                ? "border-[#d4a574] text-[#d4a574]"
                : "border-transparent text-[#7a6d5a] hover:text-[#b8ac96]"
            }`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "mao" && <MaoCalculator />}
      {tab === "contract" && <ContractGenerator />}
      {tab === "buyers" && <CashBuyersTab />}
      {tab === "deals" && <DealMarketplaceTab />}
      {tab === "signatures" && <SignaturesTab />}
    </div>
  );
}

/* ===================== MAO Calculator ===================== */
function MaoCalculator() {
  const [arv, setArv] = useState(300000);
  const [repair, setRepair] = useState(40000);
  const [fee, setFee] = useState(15000);
  const [margin, setMargin] = useState(30);
  const [result, setResult] = useState(null);

  const calc = async () => {
    try {
      const r = await api.post("/wholesale/mao", { arv, repair_costs: repair, wholesaler_fee: fee, buyer_margin_pct: margin });
      setResult(r.data);
    } catch (e) { toast.error("Calculation failed"); }
  };

  useEffect(() => { calc(); /* eslint-disable-next-line */ }, [arv, repair, fee, margin]);

  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="mao-calc">
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4">
        <div className="font-display text-lg font-semibold">Deal Numbers</div>
        <NumField label="After-Repair Value (ARV)" value={arv} onChange={setArv} testid="mao-arv" />
        <NumField label="Estimated Repair Costs" value={repair} onChange={setRepair} testid="mao-repair" />
        <NumField label="Your Assignment Fee" value={fee} onChange={setFee} testid="mao-fee" />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1 flex justify-between">
            <span>Buyer's Profit Margin</span>
            <span className="font-mono text-[#d4a574]">{margin}%</span>
          </label>
          <input type="range" min="10" max="50" value={margin} onChange={(e) => setMargin(Number(e.target.value))} data-testid="mao-margin" className="w-full accent-[#d4a574]" />
          <div className="text-[10px] text-[#7a6d5a] mt-1 font-mono">70% Rule uses 30% margin (industry standard for cash flippers)</div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="p-5 border border-[#34d399]/30 rounded-lg bg-[#34d399]/5" data-testid="mao-result">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#34d399] font-semibold mb-1">Maximum Allowable Offer (MAO)</div>
          <div className="font-display text-4xl font-bold text-[#34d399] font-mono" data-testid="mao-value">{currency(result?.mao || 0)}</div>
          <div className="text-[11px] text-[#7a6d5a] mt-2 font-mono">This is the MOST you should offer the seller to keep the deal profitable for you and attractive to your cash buyer.</div>
        </div>
        <BreakdownRow label="ARV" value={result?.arv} />
        <BreakdownRow label={`Less buyer's ${margin}% margin`} value={-1 * (result?.buyer_profit_target || 0)} />
        <BreakdownRow label="Less repair costs" value={-1 * (result?.repair_costs || 0)} />
        <BreakdownRow label="Less your assignment fee" value={-1 * (result?.wholesaler_fee || 0)} />
        <BreakdownRow label="= Your MAO" value={result?.mao} highlight />
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6d5a]">$</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          data-testid={testid}
          className="w-full bg-[#241d16] border border-[#33291f] rounded pl-7 pr-3 py-2 text-[14px] font-mono text-[#f4ede2] focus:outline-none focus:border-[#d4a574]"
        />
      </div>
    </div>
  );
}
function BreakdownRow({ label, value, highlight }) {
  return (
    <div className={`flex justify-between items-center px-4 py-2 rounded ${highlight ? "bg-[#d4a574]/10 border border-[#d4a574]/30" : "bg-[#1c1712] border border-[#33291f]"}`}>
      <span className="text-[12px] text-[#b8ac96]">{label}</span>
      <span className={`font-mono font-semibold ${highlight ? "text-[#d4a574] text-lg" : "text-[#f4ede2]"}`}>{currency(value || 0)}</span>
    </div>
  );
}

/* ===================== Contract Generator ===================== */
function ContractGenerator() {
  const [f, setF] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    seller_name: "", seller_address: "", seller_phone: "", seller_email: "",
    buyer_name: "", buyer_entity: "", buyer_address: "", buyer_phone: "", buyer_email: "",
    property_address: "", property_apn: "", property_legal_desc: "",
    purchase_price: 150000, earnest_money: 500, closing_date: "",
    inspection_period_days: 10,
    title_company_name: "", title_company_address: "",
    assignment_fee: 15000,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });

  const download = async () => {
    if (!f.property_address || !f.seller_name || !f.buyer_name) {
      toast.error("Fill Seller name, Buyer name, and Property address at minimum");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/wholesale/contract/pdf", f, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Assignment-Contract-${f.property_address.replace(/[^a-z0-9]/gi, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Contract downloaded. Have your attorney review before signing.");
    } catch (e) {
      if (e.response?.status === 403) toast.error("Contract generation requires an active subscription");
      else toast.error("Failed to generate contract");
    } finally { setBusy(false); }
  };

  const Section = ({ title, children }) => (
    <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5 space-y-3">
      <div className="font-display text-md font-semibold text-[#d4a574]">{title}</div>
      {children}
    </div>
  );
  const Row = ({ label, value, onChange, type = "text", ph, testid, className = "" }) => (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={ph} data-testid={testid}
        className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] text-[#f4ede2] focus:outline-none focus:border-[#d4a574]" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="contract-gen">
      <div className="p-4 rounded-lg border border-[#c94a3a]/40 bg-[#c94a3a]/5 flex gap-3">
        <AlertTriangle size={18} className="text-[#c94a3a] shrink-0 mt-0.5" />
        <div className="text-[12px] text-[#f4ede2]">
          <strong>Legal Disclaimer:</strong> This generator produces a STARTING DRAFT for informational purposes only. Real-estate law varies by state. Have a licensed attorney in the property's jurisdiction review and modify before signing. The Close Out is not a law firm.
        </div>
      </div>

      <Section title="Seller Information">
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Full Name / Entity" value={f.seller_name} onChange={set("seller_name")} testid="cg-seller-name" ph="John Doe" />
          <Row label="Mailing Address" value={f.seller_address} onChange={set("seller_address")} testid="cg-seller-addr" />
          <Row label="Phone" value={f.seller_phone} onChange={set("seller_phone")} testid="cg-seller-phone" />
          <Row label="Email" value={f.seller_email} onChange={set("seller_email")} type="email" testid="cg-seller-email" />
        </div>
      </Section>

      <Section title="Buyer (You / Your Entity)">
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Buyer Name" value={f.buyer_name} onChange={set("buyer_name")} testid="cg-buyer-name" />
          <Row label="Entity (LLC / Corp)" value={f.buyer_entity} onChange={set("buyer_entity")} testid="cg-buyer-entity" ph="Acme Real Estate, LLC" />
          <Row label="Address" value={f.buyer_address} onChange={set("buyer_address")} testid="cg-buyer-addr" />
          <Row label="Phone" value={f.buyer_phone} onChange={set("buyer_phone")} testid="cg-buyer-phone" />
          <Row label="Email" value={f.buyer_email} onChange={set("buyer_email")} type="email" testid="cg-buyer-email" className="md:col-span-2" />
        </div>
      </Section>

      <Section title="Property">
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Property Address" value={f.property_address} onChange={set("property_address")} testid="cg-prop-addr" className="md:col-span-2" ph="123 Main St, Dallas, TX 75232" />
          <Row label="APN / Parcel #" value={f.property_apn} onChange={set("property_apn")} testid="cg-prop-apn" />
          <Row label="Legal Description (or attach Exhibit A)" value={f.property_legal_desc} onChange={set("property_legal_desc")} testid="cg-prop-legal" />
        </div>
      </Section>

      <Section title="Financial Terms">
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Purchase Price ($)" value={f.purchase_price} onChange={setNum("purchase_price")} type="number" testid="cg-price" />
          <Row label="Earnest Money ($)" value={f.earnest_money} onChange={setNum("earnest_money")} type="number" testid="cg-em" />
          <Row label="Assignment Fee ($)" value={f.assignment_fee} onChange={setNum("assignment_fee")} type="number" testid="cg-fee" />
          <Row label="Closing Date" value={f.closing_date} onChange={set("closing_date")} type="date" testid="cg-closing" />
          <Row label="Inspection Period (days)" value={f.inspection_period_days} onChange={setNum("inspection_period_days")} type="number" testid="cg-inspection" />
        </div>
      </Section>

      <Section title="Escrow / Title">
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Title Company Name" value={f.title_company_name} onChange={set("title_company_name")} testid="cg-title-name" />
          <Row label="Title Company Address" value={f.title_company_address} onChange={set("title_company_address")} testid="cg-title-addr" />
        </div>
      </Section>

      <button
        onClick={download}
        disabled={busy}
        data-testid="cg-download"
        className="w-full py-3.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[14px] hover:bg-[#e8b988] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
        {busy ? "Generating…" : "Download Assignment Contract PDF"}
      </button>

      <ESignatureBar fields={f} />
    </div>
  );
}

/* ===================== E-Signature Send Bar (inside Contract tab) ===================== */
function ESignatureBar({ fields }) {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/wholesale/esign/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!fields.seller_email || !fields.seller_email.includes("@")) {
      toast.error("Seller email is required to send for e-signature");
      return;
    }
    if (!fields.property_address || !fields.seller_name || !fields.buyer_name) {
      toast.error("Fill seller name, buyer name, property address, and seller email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/wholesale/contract/send-for-signature", fields);
      if (r.data.ok) {
        toast.success(`Contract sent to ${fields.seller_email}. Watch the "Signed Docs" tab for status.`);
      } else {
        toast.error(r.data.error || "Send failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "E-signature send failed");
    } finally { setSending(false); }
  };

  if (!status?.configured) {
    return (
      <div className="p-3 rounded border border-[#33291f] bg-[#1c1712] flex items-center gap-3 text-[12px] text-[#7a6d5a]">
        <PenSquare size={14} /> E-signature service not configured. Contact your admin.
      </div>
    );
  }

  return (
    <div className="border border-[#d4a574]/40 bg-gradient-to-br from-[#241d16] to-[#1c1712] rounded-lg p-4" data-testid="esign-bar">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <PenSquare size={16} className="text-[#d4a574]" />
          <div>
            <div className="font-display font-semibold text-[14px]">Send for E-Signature</div>
            <div className="text-[11px] text-[#7a6d5a]">Emails the seller a signing link via Dropbox Sign</div>
          </div>
        </div>
        <div className="text-[10px] font-mono text-[#7a6d5a]">
          {status.used_this_month}/3 used this month {status.test_mode && <span className="text-[#d4a574]"> · TEST MODE</span>}
        </div>
      </div>
      <button
        onClick={send}
        disabled={sending}
        data-testid="cg-send-esign"
        className="w-full py-3 rounded border border-[#d4a574] text-[#d4a574] font-semibold text-[13px] hover:bg-[#d4a574]/10 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {sending ? "Sending…" : "Email Seller for E-Signature"}
      </button>
      {status.test_mode && (
        <div className="text-[10px] font-mono text-[#c94a3a] mt-2">
          ⚠ Test mode: signatures are NOT legally binding. In test mode Dropbox Sign only allows sending to the Dropbox Sign account owner's email.
        </div>
      )}
    </div>
  );
}

/* ===================== Signatures Tab ===================== */
const STATUS_STYLES = {
  sent: { color: "#d4a574", label: "Sent" },
  viewed: { color: "#e8b988", label: "Viewed" },
  partially_signed: { color: "#e8b988", label: "Partial" },
  all_signed: { color: "#34d399", label: "Signed" },
  downloadable: { color: "#34d399", label: "Ready" },
  downloaded: { color: "#34d399", label: "Complete" },
  declined: { color: "#f87171", label: "Declined" },
  canceled: { color: "#7a6d5a", label: "Canceled" },
};

function SignaturesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [canceling, setCanceling] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/wholesale/signatures")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const downloadPdf = async (sig) => {
    setDownloading(sig.id);
    try {
      const r = await api.get(`/wholesale/signatures/${sig.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `signed-${sig.property_address.replace(/[^a-z0-9]/gi, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Signed PDF not yet ready" : "Download failed");
    } finally { setDownloading(null); }
  };

  const cancel = async (sig) => {
    if (!window.confirm(`Cancel signature request from ${sig.signer_email}?`)) return;
    setCanceling(sig.id);
    try {
      await api.post(`/wholesale/signatures/${sig.id}/cancel`, {});
      toast.success("Request canceled");
      load();
    } catch { toast.error("Cancel failed"); } finally { setCanceling(null); }
  };

  return (
    <div className="space-y-4" data-testid="signatures-tab">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-display text-lg font-semibold">Signature Requests</div>
          <div className="text-[11px] text-[#7a6d5a]">Track e-signature requests sent to sellers. Updates in real-time via webhook.</div>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#4a3a2b] text-[11px] text-[#d4a574] hover:border-[#d4a574]">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[#d4a574]" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#33291f] rounded-lg p-10 text-center bg-[#1c1712]/40" data-testid="no-signatures">
          <PenSquare size={28} className="mx-auto text-[#7a6d5a] mb-3" />
          <div className="text-[14px] text-[#b8ac96] mb-2">No signature requests yet.</div>
          <div className="text-[12px] text-[#7a6d5a]">Go to the Assignment Contract tab, fill in seller info, and click "Email Seller for E-Signature".</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((s) => {
            const style = STATUS_STYLES[s.status] || { color: "#7a6d5a", label: s.status };
            const canDownload = ["all_signed", "downloadable", "downloaded"].includes(s.status);
            const canCancel = ["sent", "viewed", "partially_signed"].includes(s.status);
            return (
              <div key={s.id} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-4" data-testid={`sig-row-${s.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded border font-semibold tracking-wider" style={{ color: style.color, borderColor: style.color + "60", background: style.color + "15" }}>{style.label}</span>
                      {s.test_mode && <span className="text-[9px] uppercase text-[#c94a3a] font-mono">TEST</span>}
                      <span className="text-[11px] text-[#7a6d5a] font-mono">{s.created_at?.slice(0, 10)}</span>
                    </div>
                    <div className="font-display text-[15px] font-semibold">{s.property_address}</div>
                    <div className="text-[12px] text-[#b8ac96] flex items-center gap-1.5 mt-1">
                      <Mail size={11} /> {s.signer_name} · {s.signer_email}
                    </div>
                    <div className="flex gap-4 mt-2 text-[11px] text-[#7a6d5a] font-mono flex-wrap">
                      {s.purchase_price && <span>Price: <span className="text-[#d4a574]">${Number(s.purchase_price).toLocaleString()}</span></span>}
                      {s.assignment_fee && <span>Fee: <span className="text-[#d4a574]">${Number(s.assignment_fee).toLocaleString()}</span></span>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {canDownload && (
                      <button onClick={() => downloadPdf(s)} disabled={downloading === s.id} data-testid={`sig-download-${s.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[11px] hover:bg-[#e8b988] disabled:opacity-60">
                        {downloading === s.id ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} Download Signed PDF
                      </button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancel(s)} disabled={canceling === s.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#4a3a2b] text-[11px] text-[#f87171] hover:border-[#f87171]">
                        {canceling === s.id ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />} Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ===================== Cash Buyers Tab ===================== */
function CashBuyersTab() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    budget_min: "", budget_max: "", markets: "", property_types: "", notes: "",
  });

  const load = () => {
    setLoading(true);
    api.get("/wholesale/cash-buyers")
      .then((r) => setBuyers(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        ...form,
        markets: form.markets.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        property_types: form.property_types.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      await api.post("/wholesale/cash-buyers", payload);
      toast.success(`Added ${form.name}`);
      setForm({ name: "", email: "", phone: "", company: "", budget_min: "", budget_max: "", markets: "", property_types: "", notes: "" });
      load();
    } catch { toast.error("Failed to add buyer"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this cash buyer?")) return;
    await api.delete(`/wholesale/cash-buyers/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-5" data-testid="buyers-tab">
      <form onSubmit={create} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5 space-y-3" data-testid="add-buyer-form">
        <div className="font-display text-md font-semibold text-[#d4a574] flex items-center gap-2"><Plus size={14} /> Add Cash Buyer</div>
        <div className="grid md:grid-cols-3 gap-3">
          <input required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ab-name" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ab-email" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input type="number" placeholder="Budget min ($)" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input type="number" placeholder="Budget max ($)" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <input placeholder="Markets (comma-sep: TX,FL,90210)" value={form.markets} onChange={(e) => setForm({ ...form, markets: e.target.value })} data-testid="ab-markets" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574] md:col-span-2" />
          <input placeholder="Property types (residential, sfr, warehouse)" value={form.property_types} onChange={(e) => setForm({ ...form, property_types: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
        </div>
        <button type="submit" data-testid="ab-submit" className="px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988]">Add Buyer</button>
      </form>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f] font-display text-lg font-semibold flex justify-between items-center">
          <span>Your Cash Buyers ({buyers.length})</span>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[#d4a574]" /></div>
        ) : buyers.length === 0 ? (
          <div className="p-8 text-center text-[#7a6d5a] text-sm" data-testid="no-buyers">Add your first cash buyer above. When you post a deal, matching buyers get an email blast automatically.</div>
        ) : (
          <table className="w-full text-[13px]" data-testid="buyers-table">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Budget</th><th className="text-left px-4 py-2">Markets</th><th className="text-left px-4 py-2">Types</th><th></th></tr>
            </thead>
            <tbody>
              {buyers.map((b) => (
                <tr key={b.id} className="border-t border-[#33291f]" data-testid={`buyer-row-${b.id}`}>
                  <td className="px-4 py-2"><div className="font-semibold">{b.name}</div>{b.company && <div className="text-[10px] text-[#7a6d5a]">{b.company}</div>}</td>
                  <td className="px-4 py-2 text-[11px] font-mono text-[#b8ac96]">{b.email}<br/>{b.phone}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-[#d4a574]">{b.budget_min ? currency(b.budget_min) : "—"} → {b.budget_max ? currency(b.budget_max) : "—"}</td>
                  <td className="px-4 py-2 text-[11px] text-[#b8ac96]">{(b.markets || []).join(", ") || "any"}</td>
                  <td className="px-4 py-2 text-[11px] text-[#b8ac96]">{(b.property_types || []).join(", ") || "any"}</td>
                  <td className="px-4 py-2 text-right"><button onClick={() => del(b.id)} className="p-1.5 text-[#b8ac96] hover:text-[#f87171]"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ===================== Deal Marketplace Tab ===================== */
function DealMarketplaceTab() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [blasting, setBlasting] = useState(null);
  const [form, setForm] = useState({
    property_address: "", city: "", state: "", zip: "", property_type: "",
    beds: "", baths: "", sqft: "",
    arv: 300000, repair_estimate: 40000, purchase_price: 150000,
    assignment_fee: 15000, closing_date: "", highlights: "",
  });

  const load = () => {
    setLoading(true);
    api.get("/wholesale/deals")
      .then((r) => setDeals(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const asking = Number(form.purchase_price || 0) + Number(form.assignment_fee || 0);

  const create = async (e) => {
    e.preventDefault();
    if (!form.property_address) { toast.error("Property address required"); return; }
    try {
      await api.post("/wholesale/deals", { ...form, asking_price: asking });
      toast.success("Deal posted");
      setShowForm(false);
      setForm({ property_address: "", city: "", state: "", zip: "", property_type: "", beds: "", baths: "", sqft: "", arv: 300000, repair_estimate: 40000, purchase_price: 150000, assignment_fee: 15000, closing_date: "", highlights: "" });
      load();
    } catch (e) {
      if (e.response?.status === 403) toast.error("Requires an active subscription");
      else toast.error("Failed to post deal");
    }
  };

  const blast = async (id) => {
    setBlasting(id);
    try {
      const r = await api.post(`/wholesale/deals/${id}/blast`, {});
      toast.success(r.data.matched === 0 ? r.data.message : `Blasted to ${r.data.sent} of ${r.data.matched} matching buyers`);
      load();
    } catch (e) { toast.error("Blast failed"); } finally { setBlasting(null); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/deal/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Deal link copied");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    await api.delete(`/wholesale/deals/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="deals-tab">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-display text-lg font-semibold">Your Assignable Deals</div>
          <div className="text-[11px] text-[#7a6d5a]">Post a contract, blast it to your cash buyer list, track views + inquiries.</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="post-deal-btn" className="px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988] flex items-center gap-2">
          <Plus size={13} /> {showForm ? "Cancel" : "Post New Deal"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5 space-y-3" data-testid="post-deal-form">
          <div className="grid md:grid-cols-4 gap-3">
            <input required placeholder="Property address *" value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} data-testid="pd-addr" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574] md:col-span-2" />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input placeholder="State (2-letter)" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid="pd-state" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] uppercase focus:outline-none focus:border-[#d4a574]" />
            <input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input placeholder="Property type (SFR, warehouse…)" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="Beds" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="Baths" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="ARV ($)" value={form.arv} onChange={(e) => setForm({ ...form, arv: e.target.value })} data-testid="pd-arv" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="Repair est ($)" value={form.repair_estimate} onChange={(e) => setForm({ ...form, repair_estimate: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="Purchase price ($)" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="pd-purchase" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="number" placeholder="Assignment fee ($)" value={form.assignment_fee} onChange={(e) => setForm({ ...form, assignment_fee: e.target.value })} data-testid="pd-fee" className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            <input type="date" placeholder="Closing date" value={form.closing_date} onChange={(e) => setForm({ ...form, closing_date: e.target.value })} className="bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
          <textarea rows={3} placeholder="Highlights — why is this a great deal? (recent comps, seller motivation, condition…)" value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          <div className="flex items-center justify-between">
            <div className="text-[12px] text-[#b8ac96]">Buyer's total (asking price): <span className="font-mono text-[#d4a574] font-bold">{currency(asking)}</span></div>
            <button type="submit" data-testid="pd-submit" className="px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988]">Post Deal</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[#d4a574]" /></div>
      ) : deals.length === 0 ? (
        <div className="border border-dashed border-[#33291f] rounded-lg p-10 text-center bg-[#1c1712]/40" data-testid="no-deals">
          <Megaphone size={28} className="mx-auto text-[#7a6d5a] mb-3" />
          <div className="text-[14px] text-[#b8ac96]">No deals posted yet. Lock a contract, then post it here to blast to your buyer list.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {deals.map((d) => (
            <div key={d.id} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-4" data-testid={`deal-card-${d.id}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="flex-1">
                  <div className="font-display text-lg font-semibold">{d.property_address}</div>
                  <div className="text-[12px] text-[#7a6d5a] font-mono">{d.city}, {d.state} {d.zip} · {d.property_type || "—"}</div>
                  <div className="flex gap-4 mt-3 text-[12px] flex-wrap">
                    <div><span className="text-[#7a6d5a]">ARV:</span> <span className="font-mono text-[#34d399] font-bold">{currency(d.arv)}</span></div>
                    <div><span className="text-[#7a6d5a]">Asking:</span> <span className="font-mono text-[#d4a574] font-bold">{currency(d.asking_price)}</span></div>
                    <div><span className="text-[#7a6d5a]">Fee:</span> <span className="font-mono text-[#e8b988] font-bold">{currency(d.assignment_fee)}</span></div>
                    <div><span className="text-[#7a6d5a]">Views:</span> <span className="font-mono">{d.views || 0}</span></div>
                    <div><span className="text-[#7a6d5a]">Inquiries:</span> <span className="font-mono">{d.inquiries?.length || 0}</span></div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a href={`/deal/${d.share_code}`} target="_blank" rel="noreferrer" data-testid={`deal-view-${d.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#4a3a2b] text-[11px] text-[#d4a574] hover:border-[#d4a574]"><ExternalLink size={11} /> View</a>
                  <button onClick={() => copyLink(d.share_code)} data-testid={`deal-copy-${d.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#4a3a2b] text-[11px] text-[#d4a574] hover:border-[#d4a574]"><Copy size={11} /> Copy link</button>
                  <button onClick={() => blast(d.id)} disabled={blasting === d.id} data-testid={`deal-blast-${d.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[11px] hover:bg-[#e8b988]">
                    {blasting === d.id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Blast to buyers
                  </button>
                  <button onClick={() => del(d.id)} className="p-1.5 text-[#b8ac96] hover:text-[#f87171]"><Trash2 size={13} /></button>
                </div>
              </div>
              {d.inquiries?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#33291f]">
                  <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-2">Recent inquiries</div>
                  {d.inquiries.slice(-3).map((i, k) => (
                    <div key={k} className="text-[11px] text-[#b8ac96]">
                      <strong className="text-[#f4ede2]">{i.name}</strong> · {i.email || i.phone} · {i.message?.slice(0, 80)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
